import { V2_BUTTON_SIZES } from "components/designV2/constants";
import { CustomIcon } from "components/icon/customIcon.component";
import type { ColorV2 } from "config/colors";
import { TypographyV2 } from "config/font";
import React from "react";
import styled, { css, keyframes } from "styled-components";

export type ButtonSize = "large" | "small";

/** Visual style tokens for base button variants. */
export interface BaseButtonStyle {
  backgroundColor: string;
  textColor: string;
  borderColor: string;
  hoverBackgroundColor: string;
  activeBackgroundColor: string;
  disabledBackgroundColor: string;
  disabledTextColor: string;
  disabledBorderColor: string;
  iconColor: ColorV2;
  disabledIconColor: ColorV2;
}

/** Props for the shared base button component. */
export interface BaseButtonProps extends Omit<
  React.ButtonHTMLAttributes<HTMLButtonElement>,
  "type"
> {
  label?: string;
  iconName?: string;
  size?: ButtonSize;
  type?: "button" | "submit" | "reset";
  styles: BaseButtonStyle;
  readonly expandIconLabelOnHover?: boolean;
  isLoading?: boolean;
}

const SIZE_CONFIG = V2_BUTTON_SIZES;

/** Shared base button for primary/secondary variants. */
export const BaseButton = ({
  label,
  iconName,
  size = "large",
  type = "button",
  disabled,
  expandIconLabelOnHover = true,
  isLoading = false,
  styles,
  ...rest
}: BaseButtonProps): JSX.Element => {
  const ariaLabel = rest["aria-label"];
  const isIconOnly = Boolean(iconName) && !label;
  const hoverLabel = label ?? (expandIconLabelOnHover ? ariaLabel : undefined);
  const revealLabelOnHover = isIconOnly && Boolean(hoverLabel);
  const isDisabled = Boolean(disabled || isLoading);
  const iconColor =
    isDisabled && !isLoading ? styles.disabledIconColor : styles.iconColor;
  const spinnerSize = SIZE_CONFIG[size].iconSize;
  const spinnerColor = isLoading ? styles.iconColor : styles.textColor;

  return (
    <Button
      type={type}
      disabled={isDisabled}
      $size={size}
      $iconOnly={isIconOnly}
      $revealLabelOnHover={revealLabelOnHover}
      $styles={styles}
      $isLoading={isLoading}
      aria-busy={isLoading}
      {...rest}
    >
      <Content $hidden={isLoading} $revealOnHover={revealLabelOnHover}>
        {iconName && (
          <CustomIcon
            name={iconName}
            size={`${SIZE_CONFIG[size].iconSize}px`}
            color={iconColor}
          />
        )}
        {hoverLabel && (
          <Label $revealOnHover={revealLabelOnHover}>{hoverLabel}</Label>
        )}
      </Content>
      {isLoading && (
        <Spinner $size={spinnerSize} $color={spinnerColor} aria-hidden="true" />
      )}
    </Button>
  );
};

const Button = styled.button<{
  $size: ButtonSize;
  $iconOnly: boolean;
  $revealLabelOnHover: boolean;
  $styles: BaseButtonStyle;
  $isLoading: boolean;
}>`
  ${TypographyV2.b1}
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  position: relative;
  border: 1.5px solid ${(props) => props.$styles.borderColor};
  border-radius: 999px;
  height: ${(props) => SIZE_CONFIG[props.$size].height}px;
  padding: ${(props) =>
    `${SIZE_CONFIG[props.$size].paddingY}px ${
      SIZE_CONFIG[props.$size].paddingX
    }px`};
  max-width: 100%;
  background-color: ${(props) => props.$styles.backgroundColor};
  color: ${(props) => props.$styles.textColor};
  cursor: pointer;
  transition:
    background-color 0.15s ease,
    color 0.15s ease,
    border-color 0.15s ease,
    max-width 0.28s cubic-bezier(0.2, 0.8, 0.2, 1),
    padding 0.28s cubic-bezier(0.2, 0.8, 0.2, 1),
    gap 0.28s cubic-bezier(0.2, 0.8, 0.2, 1);
  will-change: max-width, padding, gap;

  ${(props) =>
    props.$iconOnly &&
    css`
      overflow: hidden;
      width: ${SIZE_CONFIG[props.$size].height + 12}px;
      min-width: ${SIZE_CONFIG[props.$size].height + 12}px;
      max-width: ${SIZE_CONFIG[props.$size].height + 12}px;
      height: ${SIZE_CONFIG[props.$size].height}px;
      min-height: ${SIZE_CONFIG[props.$size].height}px;
      max-height: ${SIZE_CONFIG[props.$size].height}px;
      border-radius: ${
        SIZE_CONFIG[props.$size].height / 2 - (props.$size === "large" ? 4 : 3)
      }px /
        ${SIZE_CONFIG[props.$size].height / 2}px;
      padding: 0;
      gap: 0;
    `}

  ${(props) =>
    props.$revealLabelOnHover &&
    css`
      &:hover:not(:disabled) {
        width: auto;
        max-width: 360px;
        padding: ${SIZE_CONFIG[props.$size].paddingY}px
          ${SIZE_CONFIG[props.$size].paddingX}px;
        gap: 8px;
      }
    `}

  &:hover:not(:disabled) {
    background-color: ${(props) => props.$styles.hoverBackgroundColor};
  }

  &:active:not(:disabled) {
    background-color: ${(props) => props.$styles.activeBackgroundColor};
  }

  &:disabled {
    background-color: ${(props) => props.$styles.disabledBackgroundColor};
    border-color: ${(props) => props.$styles.disabledBorderColor};
    color: ${(props) => props.$styles.disabledTextColor};
    cursor: not-allowed;
  }

  ${(props) =>
    props.$isLoading &&
    css`
      cursor: progress;
      pointer-events: none;

      &:disabled {
        background-color: ${props.$styles.backgroundColor};
        border-color: ${props.$styles.borderColor};
        color: ${props.$styles.textColor};
      }
    `}
`;

const Content = styled.span<{ $hidden: boolean; $revealOnHover: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: ${(props) => (props.$revealOnHover ? 0 : 8)}px;
  opacity: ${(props) => (props.$hidden ? 0 : 1)};
  transition: opacity 0.15s ease;
`;

const spin = keyframes`
  to {
    transform: rotate(360deg);
  }
`;

const Spinner = styled.span<{ $size: number; $color: string }>`
  position: absolute;
  width: ${(props) => props.$size}px;
  height: ${(props) => props.$size}px;
  border-radius: 999px;
  border: 2px solid transparent;
  border-top-color: ${(props) => props.$color};
  border-right-color: ${(props) => props.$color};
  animation: ${spin} 0.8s linear infinite;
`;

const Label = styled.span<{ $revealOnHover: boolean }>`
  ${TypographyV2.b1}
  color: inherit;
  transition:
    opacity 0.28s cubic-bezier(0.2, 0.8, 0.2, 1),
    transform 0.28s cubic-bezier(0.2, 0.8, 0.2, 1),
    max-width 0.28s cubic-bezier(0.2, 0.8, 0.2, 1),
    margin-left 0.28s cubic-bezier(0.2, 0.8, 0.2, 1);
  will-change: opacity, transform, max-width;

  ${(props) =>
    props.$revealOnHover &&
    css`
      opacity: 0;
      transform: translateX(-8px);
      max-width: 0;
      margin-left: 0;
      overflow: hidden;
      white-space: nowrap;

      ${Button}:hover:not(:disabled) & {
        opacity: 1;
        transform: translateX(0);
        max-width: 280px;
        margin-left: 8px;
      }
    `}
`;
